import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Eye, AlertTriangle, Plus, MoreVertical } from 'lucide-react';
import type { TenantFilamentWithDetails } from '@maker-toolchest/shared';
import { SearchInput } from '@/components/SearchInput';
import { Select } from '@/components/Select';
import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Pagination } from '@/components/Pagination';
import { FilterBar } from '@/components/FilterBar';
import { DropdownMenu } from '@/components/DropdownMenu';
import { useTenantFilaments, useRemoveFilamentFromLibrary, useFilamentLookups } from '../hooks';
import type { TenantFilamentQueryParams } from '../api';
import { ColorSwatches } from './ColorSwatches';

interface MyFilamentsListProps {
  onViewFilament?: (filament: TenantFilamentWithDetails) => void;
  onAddSpools?: (tenantFilamentId: string) => void;
}

export function MyFilamentsList({ onViewFilament, onAddSpools }: MyFilamentsListProps) {
  const [filters, setFilters] = useState<TenantFilamentQueryParams>({
    page: 1,
    limit: 20,
    sortBy: 'addedAt',
    sortOrder: 'desc',
  });
  const [deleteTarget, setDeleteTarget] = useState<TenantFilamentWithDetails | null>(null);
  const [searchKey, setSearchKey] = useState(0);

  const { data, isLoading } = useTenantFilaments(filters, {
    placeholderData: (previousData) => previousData,
  });
  const { types, manufacturers, isLoading: lookupsLoading } = useFilamentLookups();
  const removeMutation = useRemoveFilamentFromLibrary();

  const filaments = data?.items || [];
  const pagination = data?.pagination;

  const handleSearch = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search: search || undefined, page: 1 }));
  }, []);

  const handleFilterChange = useCallback((key: keyof TenantFilamentQueryParams, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }));
  }, []);

  const handleRemove = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await removeMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // Error handled by mutation
    }
  }, [deleteTarget, removeMutation]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      page: 1,
      limit: 20,
      sortBy: 'addedAt',
      sortOrder: 'desc',
    });
  }, []);

  const handleApplyFilters = useCallback((newFilters: Record<string, any>) => {
    // Set all filters including search
    setFilters(prev => ({
      ...prev,
      search: newFilters.search || undefined,
      typeId: newFilters.typeId || undefined,
      manufacturerId: newFilters.manufacturerId || undefined,
      sortBy: newFilters.sortBy || prev.sortBy,
      page: 1,
    }));

    // Force remount of SearchInput to clear stale debounced state
    setSearchKey(prev => prev + 1);
  }, []);

  const hasActiveFilters = !!(
    filters.search ||
    filters.typeId ||
    filters.manufacturerId
  );

  // Build options for selects - memoized to prevent unnecessary re-renders
  const typeOptions = useMemo(
    () => [
      { value: '', label: 'All Types' },
      ...types.map((type) => ({ value: type.id, label: type.name })),
    ],
    [types]
  );

  const manufacturerOptions = useMemo(
    () => [
      { value: '', label: 'All Manufacturers' },
      ...manufacturers.map((mfr) => ({ value: mfr.id, label: mfr.name })),
    ],
    [manufacturers]
  );

  const sortByOptions = useMemo(
    () => [
      { value: 'addedAt', label: 'Date Added' },
      { value: 'name', label: 'Name' },
      { value: 'manufacturer', label: 'Manufacturer' },
      { value: 'type', label: 'Type' },
    ],
    []
  );

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <FilterBar
        searchField={
          <SearchInput
            key={searchKey}
            value={filters.search || ''}
            onChange={handleSearch}
            placeholder="Search your filaments..."
          />
        }
        filterFields={[
          <div className="w-40">
            <Select
              label="Type"
              value={filters.typeId || ''}
              onChange={(e) => handleFilterChange('typeId', e.target.value)}
              disabled={lookupsLoading}
              options={typeOptions}
            />
          </div>,
          <div className="w-48">
            <Select
              label="Manufacturer"
              value={filters.manufacturerId || ''}
              onChange={(e) => handleFilterChange('manufacturerId', e.target.value)}
              disabled={lookupsLoading}
              options={manufacturerOptions}
            />
          </div>,
          <div className="w-36">
            <Select
              label="Sort By"
              value={filters.sortBy || 'addedAt'}
              onChange={(e) => handleFilterChange('sortBy', e.target.value as TenantFilamentQueryParams['sortBy'])}
              options={sortByOptions}
            />
          </div>,
        ]}
        filterConfigs={[
          { key: 'typeId', label: 'Type', type: 'select', options: typeOptions, disabled: lookupsLoading },
          { key: 'manufacturerId', label: 'Manufacturer', type: 'select', options: manufacturerOptions, disabled: lookupsLoading },
          { key: 'sortBy', label: 'Sort By', type: 'select', options: sortByOptions },
        ]}
        filterValues={{
          search: filters.search || '',
          typeId: filters.typeId || '',
          manufacturerId: filters.manufacturerId || '',
          sortBy: filters.sortBy || 'addedAt',
        }}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Results */}
      {filaments.length === 0 ? (
        <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
          <p className="text-foreground-muted">
            {filters.search || filters.typeId || filters.manufacturerId
              ? 'No filaments found matching your criteria.'
              : "You haven't added any filaments to your library yet."}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-background-subtle">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Filament
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Diameter
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Spools
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Amount On Hand
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-background">
                {filaments.map((tf: TenantFilamentWithDetails) => (
                  <tr key={tf.id} className="hover:bg-background-subtle/50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex items-center gap-3">
                        <ColorSwatches colors={tf.filament.colorHexes} size="md" />
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {tf.filament.name}
                          </div>
                          <div className="text-xs text-foreground-muted">
                            {tf.filament.manufacturer?.name} - {tf.filament.colorName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-muted">
                      {tf.filament.type?.name}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-muted">
                      {tf.filament.diameter}mm
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {tf.spoolCount > 0 ? (
                        <Link
                          to={`/spools?tenantFilamentId=${tf.id}`}
                          className="text-primary hover:underline"
                        >
                          {tf.spoolCount}
                        </Link>
                      ) : (
                        <span className="flex items-center gap-1.5 text-warning">
                          <AlertTriangle className="h-4 w-4" />
                          <span>0</span>
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-muted">
                      {tf.totalWeight > 0 ? `${tf.totalWeight}g` : '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <div className="flex items-center justify-end">
                        <DropdownMenu
                          trigger={
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Actions"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          }
                          items={[
                            ...(onViewFilament
                              ? [
                                  {
                                    label: 'View',
                                    icon: <Eye className="h-4 w-4" />,
                                    onClick: () => onViewFilament(tf),
                                  },
                                ]
                              : []),
                            ...(onAddSpools
                              ? [
                                  {
                                    label: 'Add Spools',
                                    icon: <Plus className="h-4 w-4" />,
                                    onClick: () => onAddSpools(tf.id),
                                  },
                                ]
                              : []),
                            {
                              label: 'Remove from Library',
                              icon: <Trash2 className="h-4 w-4" />,
                              onClick: () => setDeleteTarget(tf),
                              variant: 'danger' as const,
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <Pagination
              page={pagination.page}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleRemove}
        title="Remove from Library"
        description={`Are you sure you want to remove "${deleteTarget?.filament.name}" from your library? This will also remove all associated spools.`}
        confirmText="Remove"
        variant="danger"
        loading={removeMutation.isPending}
      />
    </div>
  );
}
